/**
 * Phase 27.37.E — Central SMS Inbox
 *
 * Cross-contact conversation list (Lead / Prospect / Agent) plus
 * the Unmatched + Ambiguous queues. Right pane opens the same
 * ContactSmsCard used on the Lead detail page so the messaging
 * UX is identical everywhere.
 *
 * Route: /sms-inbox
 * Visible to KR admins (all conversations) and Agency users (only
 * their assigned Leads/Prospects + their own Agent thread — the
 * backend enforces this).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { MessageSquare, Search, RefreshCw, AlertTriangle, HelpCircle, User, UserCheck, Users, Inbox } from 'lucide-react';
import { smsInboxAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import ContactSmsCard from '@/components/sms/ContactSmsCard';
import UnmatchedResolutionPanel from '@/components/sms/UnmatchedResolutionPanel';

const FILTERS = [
  { key: 'all',        label: 'All',         icon: Inbox },
  { key: 'unread',     label: 'Unread',      icon: MessageSquare },
  { key: 'lead',       label: 'Leads',       icon: User },
  { key: 'prospect',   label: 'Prospects',   icon: Users },
  { key: 'agent_user', label: 'Agents',      icon: UserCheck },
  { key: 'unmatched',  label: 'Unmatched',   icon: HelpCircle },
  { key: 'ambiguous',  label: 'Ambiguous',   icon: AlertTriangle },
];

// Phase 27.53.M — Compact "last activity" timestamp for the inbox list so
// admins can see at a glance whether a thread went cold or is fresh.
function formatInboxTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const startOfDay = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate());
  const dayDiff = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
  if (dayDiff === 0) return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: '2-digit' });
}

export default function SmsInboxPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter === 'unread') params.unread = true;
      // The "Unmatched" tab maps to the stored contact_type "unknown"
      // (the /counts endpoint uses the same mapping). Sending the raw
      // tab key "unmatched" matched nothing → empty list despite a count.
      else if (filter !== 'all') params.contact_type = (filter === 'unmatched' ? 'unknown' : filter);
      if (q) params.q = q;
      const r = await smsInboxAPI.list(params);
      setRows(r.data?.conversations || []);
      const c = await smsInboxAPI.counts();
      setCounts(c.data || {});
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load SMS inbox'));
    } finally { setLoading(false); }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  // Deep-link: open a specific conversation when arriving from a
  // notification (/sms-inbox?c=<conversation_id>).
  useEffect(() => {
    const cid = searchParams.get('c');
    if (cid && rows.length && selected?.conversation_id !== cid) {
      const match = rows.find((r) => r.conversation_id === cid);
      if (match) openRow(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, searchParams]);

  const openRow = async (row) => {
    setSelected(row);
    if (row.unread_count > 0) {
      try {
        await smsInboxAPI.markRead(row.conversation_id);
        setRows((prev) => prev.map((r) => r.conversation_id === row.conversation_id ? { ...r, unread_count: 0 } : r));
      } catch (_) { /* no-op */ }
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="sms-inbox-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">SMS Inbox</h1>
          <p className="text-sm text-slate-500">Every SMS conversation across Leads, Prospects and Agents.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="inbox-refresh">
          <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-2" data-testid="inbox-filters">
        {FILTERS.map((f) => {
          const Icon = f.icon;
          const count = counts[f.key];
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setSelected(null); }}
              data-testid={`inbox-filter-${f.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'}`}
            >
              <Icon size={13} />
              {f.label}
              {count != null && (
                <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${active ? 'bg-white text-slate-800' : 'bg-slate-100 text-slate-700'}`}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(320px,380px)_1fr] gap-4">
        <Card className="max-h-[70vh] overflow-hidden">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Search size={14} className="text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by phone or message text…"
                className="h-8 text-sm"
                data-testid="inbox-search-input"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[calc(70vh-64px)] overflow-y-auto">
            {loading && rows.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">Loading…</div>
            )}
            {!loading && rows.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500" data-testid="inbox-empty-state">
                No conversations match this filter.
              </div>
            )}
            <ul className="divide-y divide-slate-100" data-testid="inbox-list">
              {rows.map((r) => (
                <li
                  key={r.conversation_id}
                  className={`px-3 py-2.5 cursor-pointer hover:bg-slate-50 ${selected?.conversation_id === r.conversation_id ? 'bg-slate-100' : ''}`}
                  onClick={() => openRow(r)}
                  data-testid={`inbox-row-${r.conversation_id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-800 truncate">
                      {r.display_name || <span className="italic text-slate-400">{labelForType(r.contact_type)}</span>}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.last_message_at && (
                        <span
                          className="text-[10px] text-slate-400 whitespace-nowrap"
                          title={new Date(r.last_message_at).toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          data-testid={`inbox-row-time-${r.conversation_id}`}
                        >
                          {formatInboxTime(r.last_message_at)}
                        </span>
                      )}
                      <ContactTypeBadge type={r.contact_type} />
                    </div>
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                    <span className="font-mono">{r.contact_phone}</span>
                    {r.opted_out && <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">Opted-out</Badge>}
                    {r.unread_count > 0 && <Badge className="bg-blue-600 text-white text-[10px]">{r.unread_count} unread</Badge>}
                  </div>
                  {r.last_message_preview && (
                    <p className="mt-1 text-xs text-slate-600 truncate">
                      {r.last_message_direction === 'inbound' ? '↓' : '↑'} {r.last_message_preview}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="min-h-[500px]">
          {!selected && (
            <CardContent className="h-full flex items-center justify-center text-sm text-slate-500 py-16">
              Select a conversation on the left to view messages.
            </CardContent>
          )}
          {selected && ['lead','prospect','agent_user'].includes(selected.contact_type) && (
            <CardContent className="p-4">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-800">{selected.display_name || selected.contact_phone}</div>
                  <div className="text-xs text-slate-500 font-mono">{selected.contact_phone}</div>
                </div>
                {selected.contact_id && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => goToContact(navigate, selected)}
                    data-testid="inbox-open-contact-btn"
                  >Open contact</Button>
                )}
              </div>
              <ContactSmsCard
                contactType={selected.contact_type}
                contactId={selected.contact_id}
                embedded
              />
            </CardContent>
          )}
          {selected && ['unknown','ambiguous'].includes(selected.contact_type) && (
            <CardContent className="p-4">
              <UnmatchedResolutionPanel
                conversation={selected}
                onResolved={async () => { setSelected(null); await load(); }}
              />
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}

function labelForType(t) {
  if (t === 'unknown') return 'Unmatched sender';
  if (t === 'ambiguous') return 'Ambiguous match';
  return 'Unnamed contact';
}

function goToContact(navigate, row) {
  if (row.contact_type === 'lead') return navigate(`/leads/${row.contact_id}`);
  if (row.contact_type === 'prospect') return navigate(`/prospects/${row.contact_id}`);
  if (row.contact_type === 'agent_user') return navigate(`/agents?user_id=${row.contact_id}`);
}

function ContactTypeBadge({ type }) {
  const map = {
    lead:       { label: 'Lead',      cls: 'bg-blue-50 text-blue-700 border-blue-200' },
    prospect:   { label: 'Prospect',  cls: 'bg-violet-50 text-violet-700 border-violet-200' },
    agent_user: { label: 'Agent',     cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    unknown:    { label: 'Unmatched', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    ambiguous:  { label: 'Ambiguous', cls: 'bg-orange-50 text-orange-700 border-orange-200' },
  };
  const m = map[type] || { label: type, cls: 'bg-slate-50 text-slate-700 border-slate-200' };
  return <Badge variant="outline" className={`${m.cls} border font-normal text-[10px]`}>{m.label}</Badge>;
}
